import React, { useState, useEffect } from 'react';
import SplitPane from 'react-split-pane';
import _merge from 'lodash/merge';
import { getContext, isAnimationDisabled } from 'common';

import Styled from './styles';
import {
  SPLITSCREEN_TYPE,
  PRIMARY_PANEL,
  ORIENTATION,
  DEFAULT_MIN_MAX_PANE_RATIO,
  LEFT_SQUARE_BRACKET
} from './utils/constants';

import {
  handlePaneRatio,
  hasLeftPanePrimary,
  hasRightPanePrimary,
  hasSecondaryPixelLimits,
  getAllowedMinPixelValue
} from './utils/SplitScreenHandlePaneRatio';

const SplitScreenContext = React.createContext<any>({});

export interface SplitScreenProps extends React.ComponentPropsWithRef<'div'> {
  /** Pass your content here. For example, <Pane/> */
  children: React.ReactNode;
  /** Layout of the SplitScreen - Vertical or Horizontal */
  orientation?: 'vertical' | 'horizontal';
  /** The pane that is considered the primary pane for the vertical temporary SplitScreen. */
  primaryPanel?: 'vertical' | 'horizontal';
  /** Default spilt ratio (value evaluated as percentage). If not set the left and right panes will be split to a default of 50% */
  defaultSplitRatio?: number;
  /** minRatio and maxRatio support in SplitScreen to control how far left/right or up/down the divider may be moved.
   * If the paneRatio is not set, the default values for horizontal SplitScreen are: {minRatio: 30, maxRatio: 70} and for vertical SplitScreen are: {minRatio: 16, maxRatio: 66.6}
   * If the minRatio is set to less than 5, the value of minRatio remains 5. If the maxRatio is set to greater than 95, the value of maxRatio remains 95
   * Note: if paneRatio is not set, but pixelLimitsSecondary are, then pixelLimitsSecondary will be used for the default.
   */
  paneRatio?: {
    minRatio?: number;
    maxRatio?: number;
  };
  /** SplitScreen type - permanent or temporary */
  splitScreenType?: 'temporary' | 'permanent';
  /** minPixel and maxPixel to control how far left/right or up/down the divider for the slide-in pane may move. There are not any default values, and if these properties are not set, default is to
   * use the defaultSplitRatio and paneRatio. Example {minPixel: 50, maxPixel: 150}
   * The minimum value of minPixel is 50. Any value less than 50 is ignored and the minPixel value set to 50.
   */
  pixelLimitsSecondary?: {
    minPixel: number;
    maxPixel: number;
  };
  /**
   * The width setting (example 100) to use as the default for the slide-in pane when using pixelLimitsSecondary property.
   * The minimum value of defaultPixelSize is 50. Any value less than 50 is ignored and the defaultPixelSize value set to 50.
   */
  defaultPixelSize?: number;
  /** onDragStarted callback is invoked when a drag starts. */
  onDragStarted?(): void;
  /** onDragFinished callback is invoked when a drag ends. */
  onDragFinished?(any): void;
  /** onChange callback is invoked with the current drag during a drag event. It is recommended that it is wrapped in a debounce function. */
  onChange?(any): void;
  /** Toggle/Enable the SplitScreen when SplitScreen is of type temporary */
  toggleTemporary?: boolean;
  /** If AppToolbar present set to true to take in account of the height of appToolbar */
  toolBarExists?: boolean;
  /** If true, disable all motion animation for this component.  This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any
}

/** SplitScreen, all other props will be provided to the root DOM element */
const SplitScreen = React.forwardRef<HTMLElement, SplitScreenProps>((props: SplitScreenProps, ref) =>
  getContext(({ RTL, theme, disableAnimation }) => {
    const {
      children,
      orientation,
      primaryPanel,
      splitScreenType,
      defaultSplitRatio,
      onDragStarted,
      toggleTemporary,
      onDragFinished,
      onChange,
      pixelLimitsSecondary,
      defaultPixelSize,
      toolBarExists,
      disableAnimation: propsdisableAnimation,
      theme: propTheme,
      ...otherProps
    } = props;
    const resolvedTheme = _merge({}, theme.SPLITSCREEN, propTheme);
    const [isDragging, setDragging] = useState(false);
    const [position, setPosition] = useState('center');
    const [defaultSize, setDefaultSize] = useState(defaultSplitRatio);
    const [animateSplit, setAnimateSplit] = useState(false);
    const handleKeyDown = (e) => {
      if (e.keyCode === LEFT_SQUARE_BRACKET) {
        switch (position) {
          case 'center':
            setPosition('max');
            break;
          case 'min':
            setPosition('center');
            break;
          case 'max':
            setPosition('min');
            break;
          default:
            break;
        }
      }
    };

    // sets the ratio of left and right pane in SplitScreen.
    useEffect(() => {
      let size;
      switch (position) {
        case 'center':
          setDefaultSize(defaultSplitRatio);
          break;
        case 'min':
          size = (orientation === ORIENTATION.HORIZONTAL
            ? DEFAULT_MIN_MAX_PANE_RATIO.HORIZONTAL_MIN
            : DEFAULT_MIN_MAX_PANE_RATIO.VERTICAL_MIN);
          setDefaultSize(size);
          break;
        case 'max':
          size = (orientation === ORIENTATION.HORIZONTAL
            ? DEFAULT_MIN_MAX_PANE_RATIO.HORIZONTAL_MAX
            : DEFAULT_MIN_MAX_PANE_RATIO.VERTICAL_MAX);
          setDefaultSize(size);
          break;
        default:
          setDefaultSize(DEFAULT_MIN_MAX_PANE_RATIO.EQUAL);
          break;
      }
    }, [position]);

    // toggles the animation for SplitScreen of type temporary.
    useEffect(() => {
      if (toggleTemporary) {
        setAnimateSplit(true);
      }
    }, [toggleTemporary]);

    const handleTransitionEnd = () => {
      setAnimateSplit(false);
    };

    const handleDragStarted = () => {
      setDragging(true);
      onDragStarted && onDragStarted();
    };

    const handleDragFinished = (event) => {
      setDragging(false);
      // create and fire a window resize event to resize any components within the split pane
      window.dispatchEvent(new Event('resize'));
      onDragFinished && onDragFinished(event);
    };

    let minPanelSizePixels;
    if (hasSecondaryPixelLimits(pixelLimitsSecondary)) {
      minPanelSizePixels = getAllowedMinPixelValue(pixelLimitsSecondary.minPixel);
    }

    const splitPaneProps = {
      minSize: hasSecondaryPixelLimits(pixelLimitsSecondary) ? minPanelSizePixels : undefined,
      defaultSize: hasSecondaryPixelLimits(pixelLimitsSecondary) ? getAllowedMinPixelValue(defaultPixelSize) : `${defaultSize}%`,
      pane1Style: hasRightPanePrimary(primaryPanel) ? handlePaneRatio(props) : undefined,
      pane2Style: hasLeftPanePrimary(primaryPanel) ? handlePaneRatio(props) : undefined,
    };

    return (
      <Styled.Wrapper
        tabIndex="0"
        orientation={orientation}
        screenType={splitScreenType}
        toggleSplit={toggleTemporary}
        primary={primaryPanel}
        animateIn={animateSplit}
        $disableAnimation={isAnimationDisabled(propsdisableAnimation, disableAnimation)}
        onKeyDown={handleKeyDown}
        onTransitionEnd={handleTransitionEnd}
        toolBarPresent={toolBarExists}
        ref={ref}
        $rtl={RTL}
        $theme={resolvedTheme}
        {...otherProps}
      >
        <SplitScreenContext.Provider value={{ isDragging }}>
          { /** @ts-expect-error, upgrade SplitPane, misisng children type */ }
          <SplitPane
            split={orientation}
            defaultSize={`${defaultSplitRatio}%`}
            primary={primaryPanel === PRIMARY_PANEL.RIGHT ? 'first' : 'second'}
            onDragStarted={handleDragStarted}
            onDragFinished={handleDragFinished}
            onChange={onChange}
            {...splitPaneProps}
          >
            {children}
          </SplitPane>
        </SplitScreenContext.Provider>
      </Styled.Wrapper>
    );
  }));

SplitScreen.defaultProps = {
  orientation: 'vertical',
  primaryPanel: PRIMARY_PANEL.RIGHT,
  defaultSplitRatio: 50,
  paneRatio: undefined,
  splitScreenType: SPLITSCREEN_TYPE.PERMANENT,
  pixelLimitsSecondary: undefined,
  defaultPixelSize: undefined,
  onDragStarted: undefined,
  onDragFinished: undefined,
  onChange: undefined,
  toggleTemporary: false,
  toolBarExists: false,
  disableAnimation: undefined,
  theme: {}
};

SplitScreen.displayName = 'SplitScreen';

export {
  SplitScreen as default,
  SplitScreenContext
};